import {
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useState,
} from "react";

import { Column, Row, Text } from "@hightouch/htui";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import tippy, {
  type GetReferenceClientRect,
  type Instance,
  type Placement,
} from "tippy.js";

import { groupIntoSections } from "src/components/suggestion-menu/sections";

/** How a typeahead menu shows its items and turns a picked one into a node. */
export type SuggestionMenuOptions<Item, Attrs> = {
  getKey: (item: Item) => string;
  /** The heading an item sits under. Without it the menu has no headings. */
  getSection?: (item: Item) => string;
  /** Headings that come first, in this order. */
  sectionOrder?: readonly string[];
  /** Hides the heading when every item shares one section. */
  hideLoneSection?: boolean;
  /** The row's content; the menu draws the row itself. */
  renderItem: (item: Item) => ReactNode;
  /** The attributes of the node a picked item inserts. */
  toAttrs: (item: Item) => Attrs;
  /** What the list is, for screen readers, such as "People and agents". */
  listLabel: string;
  /**
   * Shown when there's nothing to offer before anything is typed. Once a query
   * matches nothing the menu hides, so a trigger character in ordinary text
   * doesn't leave it open.
   */
  emptyMessage?: string;
  placement?: Placement;
  onOpenChange?: (isOpen: boolean) => void;
  onPopupCreate?: (popup: HTMLElement) => void;
};

/** What the suggestion plugin calls on the list for each key it doesn't handle. */
export type SuggestionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

/**
 * The popup for a tiptap suggestion: a sectioned, keyboard-driven list that
 * follows the caret.
 */
export function buildSuggestionMenu<Item, Attrs>(
  menu: SuggestionMenuOptions<Item, Attrs>,
): Pick<SuggestionOptions<Item, Attrs>, "render"> {
  return {
    render: () => {
      let renderer: ReactRenderer<
        SuggestionListHandle,
        SuggestionListProps<Item, Attrs>
      > | null = null;
      let popup: Instance[] | undefined;

      // Idempotent: Escape tears down here and again on the plugin's exit.
      const teardown = () => {
        menu.onOpenChange?.(false);
        popup?.[0]?.destroy();
        popup = undefined;
        renderer?.destroy();
        renderer = null;
      };

      return {
        onStart: (props) => {
          menu.onOpenChange?.(true);
          const created = new ReactRenderer(
            SuggestionOptionsList<Item, Attrs>,
            { props: { ...props, menu }, editor: props.editor },
          );
          renderer = created;
          if (!props.clientRect) return;
          popup = tippy("body", {
            getReferenceClientRect: props.clientRect as GetReferenceClientRect,
            appendTo: () => document.body,
            content: created.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: menu.placement ?? "bottom-start",
            onCreate: (instance) => menu.onPopupCreate?.(instance.popper),
          });
        },
        onUpdate: (props) => {
          renderer?.updateProps({ ...props, menu });
          if (!props.clientRect) return;
          popup?.[0]?.setProps({
            getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          });
        },
        onKeyDown: (props) => {
          if (props.event.key === "Escape") {
            // Torn down now rather than on the plugin's exit, which lands after
            // the keypress: outer Escape handlers read the DOM during it to see
            // whether a popup was open. Destroyed rather than hidden, since
            // `hide()` keeps the element in the DOM through the fade.
            teardown();
            return true;
          }
          return renderer?.ref?.onKeyDown(props) ?? false;
        },
        onExit: teardown,
      };
    },
  };
}

type SuggestionListProps<Item, Attrs> = Pick<
  SuggestionProps<Item, Attrs>,
  "items" | "command" | "query"
> & {
  menu: SuggestionMenuOptions<Item, Attrs>;
};

/** The menu's list: sectioned, keyboard-driven, in the order it shows items. */
export const SuggestionOptionsList = forwardRef(function SuggestionOptionsList<
  Item,
  Attrs,
>(
  { items, command, query, menu }: SuggestionListProps<Item, Attrs>,
  ref: ForwardedRef<SuggestionListHandle>,
) {
  const { getSection } = menu;
  const sections = groupIntoSections({
    items,
    getSection: getSection ?? (() => ""),
    order: menu.sectionOrder,
  });
  const showHeadings =
    getSection !== undefined &&
    !(menu.hideLoneSection && sections.length === 1);
  // The keyboard moves through items in the order they're shown.
  const shown = sections.flatMap((section) =>
    section.entries.map(({ item }) => item),
  );

  const [selected, setSelected] = useState({ items, index: 0 });
  // A new query starts the highlight back at the top.
  const selectedIndex = selected.items === items ? selected.index : 0;
  const select = (index: number) => setSelected({ items, index });

  const pick = (index: number) => {
    const item = shown[index];
    if (item !== undefined) command(menu.toAttrs(item));
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (shown.length === 0) return false;
      if (event.key === "ArrowUp") {
        select((selectedIndex + shown.length - 1) % shown.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        select((selectedIndex + 1) % shown.length);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        pick(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (shown.length === 0 && (!menu.emptyMessage || query.trim())) {
    return null;
  }

  return (
    <Column
      bg="white"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
      borderRadius="lg"
      boxShadow="lg"
      p={1}
      gap={0.5}
      minWidth={60}
      maxWidth="xs"
      maxHeight={72}
      overflowY="auto"
      role="listbox"
      aria-label={menu.listLabel}
    >
      {shown.length === 0 ? (
        <Row px={2} py={1.5}>
          <Text size="sm" color="text.tertiary">
            {menu.emptyMessage}
          </Text>
        </Row>
      ) : (
        sections.map((section, sectionIndex) => (
          <Column key={section.label} gap={0.5}>
            {showHeadings && section.label && (
              <Row px={2} pt={sectionIndex === 0 ? 0.5 : 1.5} pb={0.5}>
                <Text size="sm" fontWeight="medium" color="text.secondary">
                  {section.label}
                </Text>
              </Row>
            )}
            {section.entries.map(({ item, index }) => (
              <Row
                key={menu.getKey(item)}
                ref={
                  index === selectedIndex ? scrollIntoNearestView : undefined
                }
                as="button"
                type="button"
                role="option"
                aria-selected={index === selectedIndex}
                align="center"
                gap={2}
                px={2}
                py={1}
                borderRadius="md"
                textAlign="left"
                width="100%"
                bg={index === selectedIndex ? "base.background" : "transparent"}
                onMouseDown={(event) => {
                  // Mousedown, not click: blurring the editor would drop the range the item replaces.
                  event.preventDefault();
                  pick(index);
                }}
                // Move, not over: the list scrolling under a still mouse fires `pointerover`.
                onPointerMove={() => select(index)}
              >
                {menu.renderItem(item)}
              </Row>
            ))}
          </Column>
        ))
      )}
    </Column>
  );
}) as <Item, Attrs>(
  props: SuggestionListProps<Item, Attrs> & { ref?: Ref<SuggestionListHandle> },
) => ReactElement | null;

// The highlighted row stays in view as the keyboard moves past the list's edge.
function scrollIntoNearestView(row: HTMLElement | null) {
  row?.scrollIntoView?.({ block: "nearest" });
}
